import React, { useCallback, useEffect, useState } from "react";
import { Transition } from "@headlessui/react";
import { UseResumesReturn } from "../hooks/useResumes";
import { useAuth } from "../contexts/AuthContext";
import {
  resumeTypesService,
  type ResumeType,
} from "../services/resumeTypesService";
import { SearchableDropdown } from "./SearchableDropdown";

interface ResumeUploadModalProps {
  open: boolean;
  onClose: () => void;
  resumesHook: UseResumesReturn;
}

const MAX_FILE_SIZE_MB = 10;
const ACCEPTED_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const formatBytes = (bytes: number) => {
  if (!bytes || bytes <= 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  // Ensure index is non-negative and within bounds
  const safeIndex = Math.max(0, Math.min(i, sizes.length - 1));
  return `${(bytes / Math.pow(k, safeIndex)).toFixed(1)} ${sizes[safeIndex]}`;
};

export const ResumeUploadModal: React.FC<ResumeUploadModalProps> = ({
  open,
  onClose,
  resumesHook,
}) => {
  const { isUploading, uploadResume, uploadError, resumes, clearUploadError } =
    resumesHook;
  const { profile, updateProfile } = useAuth();
  const [selectedType, setSelectedType] = useState<string>("general");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [resumeTypes, setResumeTypes] = useState<ResumeType[]>([
    { value: "general", label: "General Resume" },
  ]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [showIndustryPrompt, setShowIndustryPrompt] = useState(false);
  const [hasAttemptedUpload, setHasAttemptedUpload] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState<string>("");
  const [industries, setIndustries] = useState<string[]>([]);
  const [loadingIndustries, setLoadingIndustries] = useState(false);
  const [isUploadingLocal, setIsUploadingLocal] = useState(false);

  const resetState = useCallback(() => {
    setSelectedType("general");
    setSelectedFile(null);
    setLocalError(null);
    setSelectedIndustry(profile?.industry || "");
    setIsUploadingLocal(false);
  }, [profile?.industry]);

  // Load industries on mount
  useEffect(() => {
    const loadIndustries = async () => {
      if (!open) return;

      setLoadingIndustries(true);
      try {
        const industriesList = await resumeTypesService.getIndustries();
        setIndustries(industriesList);
      } catch (error) {
        if (error instanceof Error) {
          // Only show error if it's not a network error (fallback will be used)
          if (
            !error.message.includes("Failed to fetch") &&
            !error.message.includes("NetworkError")
          ) {
            setLocalError(`Failed to load industries: ${error.message}`);
          }
        }
      } finally {
        setLoadingIndustries(false);
      }
    };
    loadIndustries();
  }, [open]);

  // Initialize selectedIndustry from profile when modal opens
  useEffect(() => {
    if (open) {
      setSelectedIndustry(profile?.industry || "");
    }
  }, [open, profile?.industry]);

  // Load resume types based on selected industry (from modal or profile)
  useEffect(() => {
    const loadResumeTypes = async () => {
      if (!open) return;

      setLoadingTypes(true);
      try {
        // Clear cache to ensure fresh data when industry changes
        resumeTypesService.clearCache();

        const types = await resumeTypesService.getResumeTypes(
          selectedIndustry || null,
        );
        setResumeTypes(types);
      } catch (error) {
        // Fallback to general only
        setResumeTypes([{ value: "general", label: "General Resume" }]);
        // Only show error if it's not a network error (fallback will be used)
        if (
          error instanceof Error &&
          !error.message.includes("Failed to fetch") &&
          !error.message.includes("NetworkError")
        ) {
          setLocalError(`Failed to load resume types: ${error.message}`);
        }
      } finally {
        setLoadingTypes(false);
      }
    };
    loadResumeTypes();
  }, [open, selectedIndustry]);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      resetState();
      setHasAttemptedUpload(false);
      setShowIndustryPrompt(false);
      setIsUploadingLocal(false);
      // Clear any stale upload errors from previous attempts
      clearUploadError?.();
    }
  }, [open, resetState, clearUploadError]);

  // Reset local uploading state when hook's isUploading becomes false
  useEffect(() => {
    if (!isUploading) {
      setIsUploadingLocal(false);
    }
  }, [isUploading]);

  const handleClose = useCallback(() => {
    if (isUploading || isUploadingLocal) return;
    resetState();
    onClose();
  }, [isUploading, isUploadingLocal, onClose, resetState]);

  const validateFile = useCallback((file: File) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      return "Unsupported file type. Upload PDF, DOC, or DOCX.";
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      return `File exceeds ${MAX_FILE_SIZE_MB} MB limit.`;
    }
    return null;
  }, []);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!event.target.files || event.target.files.length === 0) return;
      const file = event.target.files[0];
      const error = validateFile(file);
      if (error) {
        setLocalError(error);
        setSelectedFile(null);
        return;
      }
      setLocalError(null);
      setSelectedFile(file);
    },
    [validateFile],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (!event.dataTransfer.files || event.dataTransfer.files.length === 0)
        return;
      const file = event.dataTransfer.files[0];
      const error = validateFile(file);
      if (error) {
        setLocalError(error);
        setSelectedFile(null);
        return;
      }
      setLocalError(null);
      setSelectedFile(file);
    },
    [validateFile],
  );

  const handleUpload = useCallback(async () => {
    // CRITICAL: Set uploading state IMMEDIATELY for instant UI feedback
    // This must happen before any validation or async operations
    setIsUploadingLocal(true);

    if (!selectedFile) {
      setLocalError("Select a resume to upload.");
      setIsUploadingLocal(false);
      return;
    }

    // Check if industry is set (unless using General Resume)
    if (!selectedIndustry && selectedType !== "general") {
      setHasAttemptedUpload(true);
      setShowIndustryPrompt(true);
      setLocalError(
        "Please select your industry to upload specialized resume types. You can still upload a General Resume.",
      );
      setIsUploadingLocal(false);
      return;
    }

    try {
      // Save industry to profile if it was selected in modal and differs from profile
      if (
        selectedIndustry &&
        selectedIndustry !== profile?.industry &&
        updateProfile
      ) {
        try {
          await updateProfile({ industry: selectedIndustry });
        } catch (error) {
          console.error("Failed to save industry to profile:", error);
          // Continue with upload even if profile update fails
        }
      }

      await uploadResume(selectedFile, selectedType);
      resetState();
      setShowIndustryPrompt(false);
      setHasAttemptedUpload(false);
      setIsUploadingLocal(false);
      onClose();
    } catch (error) {
      console.error("Upload error", error);
      setIsUploadingLocal(false);
      // Error is already handled by uploadResume and set in uploadError
      // No need to set localError here as uploadError will be displayed
    }
  }, [
    onClose,
    resetState,
    selectedFile,
    selectedType,
    uploadResume,
    selectedIndustry,
    profile?.industry,
    updateProfile,
  ]);

  const displayError = localError || uploadError;

  return (
    <Transition show={open} as={React.Fragment as any}>
      <div className="fixed inset-0 z-[999] flex items-center justify-center">
        <div
          className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm transition-opacity"
          onClick={handleClose}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              handleClose();
            }
          }}
          role="button"
          tabIndex={0}
        />

        <div className="relative w-full max-w-2xl max-h-[90vh] rounded-2xl bg-white shadow-xl transition-all flex flex-col">
          <div className="border-b border-slate-200 px-6 py-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-slate-900">
                  Upload Resume
                </h2>
                <p className="mt-1 text-sm text-slate-600">
                  Add a new resume to run through ATS processing.
                </p>
              </div>
              <button
                type="button"
                onClick={handleClose}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              >
                <span className="sr-only">Close</span>×
              </button>
            </div>

            {/* Banner: Industry not set notification */}
            {!selectedIndustry && (
              <div className="mt-4 rounded-lg border border-blue-300 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <p className="font-medium">Industry Not Selected</p>
                    <p className="mt-1 text-xs">
                      Select your industry below to see specialized resume
                      types. You can still upload a General Resume.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Modal prompt: Show on first upload attempt if industry not set */}
            {showIndustryPrompt &&
              hasAttemptedUpload &&
              !selectedIndustry &&
              selectedType !== "general" && (
                <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <p className="font-medium">Industry Required</p>
                      <p className="mt-1 text-xs">
                        Please select your industry below to upload specialized
                        resume types. You can still upload a General Resume
                        without setting your industry.
                      </p>
                    </div>
                  </div>
                </div>
              )}

            {displayError && (
              <div className="mt-4 rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {displayError}
              </div>
            )}
          </div>

          <div className="space-y-6 px-6 py-6 overflow-y-auto flex-1">
            <div>
              <label
                htmlFor="industry-select"
                className="text-sm font-medium text-slate-700"
              >
                Industry
              </label>
              <SearchableDropdown
                options={industries}
                value={selectedIndustry}
                onChange={(value) => {
                  setSelectedIndustry(value);
                  setShowIndustryPrompt(false);
                  setLocalError(null);
                  setSelectedType("general");
                  // Clear cache to ensure fresh resume types are loaded
                  resumeTypesService.clearCache();
                }}
                placeholder="Search and select your industry (optional)"
                loading={loadingIndustries}
                className="w-full"
              />
              <p className="mt-1 text-xs text-slate-500">
                Select your industry to see relevant resume types. This will be
                saved to your profile.
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700">
                Resume Type
              </label>
              {loadingTypes ? (
                <div className="mt-2 text-sm text-slate-500">
                  Loading resume types...
                </div>
              ) : (
                <div className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                    {resumeTypes.map((type) => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => {
                          setSelectedType(type.value);
                          setShowIndustryPrompt(false);
                          setLocalError(null);
                        }}
                        disabled={!selectedIndustry && type.value !== "general"}
                        className={`rounded-xl border px-3 py-2 text-sm font-medium transition ${
                          selectedType === type.value
                            ? "border-primary-500 bg-primary-50 text-primary-700 shadow-sm"
                            : "border-slate-200 bg-white text-slate-600 hover:border-primary-300 hover:text-primary-600"
                        } ${
                          !selectedIndustry && type.value !== "general"
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                        title={
                          !selectedIndustry && type.value !== "general"
                            ? "Select your industry above to use this resume type"
                            : ""
                        }
                      >
                        {type.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {!selectedIndustry && (
                <p className="mt-2 text-xs text-slate-500">
                  Only "General Resume" is available. Select your industry above
                  to see more resume types.
                </p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-slate-700">
                Upload File
              </label>
              <div
                onDrop={handleDrop}
                onDragOver={(event) => event.preventDefault()}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    document.getElementById("resume-upload")?.click();
                  }
                }}
                role="button"
                tabIndex={0}
                className="mt-2 flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center transition hover:border-primary-400 hover:bg-primary-50"
              >
                <input
                  id="resume-upload"
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileChange}
                  className="hidden"
                />
                <label
                  htmlFor="resume-upload"
                  className="flex cursor-pointer flex-col items-center gap-2"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-primary-600 text-lg">
                    📄
                  </span>
                  <div>
                    {selectedFile ? (
                      <div className="text-sm font-medium text-primary-700">
                        {selectedFile.name}
                      </div>
                    ) : (
                      <div className="text-sm font-medium text-slate-700">
                        Drop your resume or click to browse
                      </div>
                    )}
                    <div className="mt-0.5 text-xs text-slate-500">
                      PDF, DOC, DOCX up to {MAX_FILE_SIZE_MB}MB
                    </div>
                    {selectedFile && (
                      <div className="mt-1 text-xs text-slate-500">
                        {formatBytes(selectedFile.size)}
                      </div>
                    )}
                  </div>
                </label>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-4 border-t border-slate-200 bg-slate-50 px-6 py-4">
            <div className="text-xs text-slate-500">
              Stored resumes: {resumes.length} / Unlimited
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleClose}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={
                  !selectedFile ||
                  isUploading ||
                  isUploadingLocal ||
                  (!selectedIndustry && selectedType !== "general")
                }
                onClick={handleUpload}
                className={`rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition flex items-center justify-center gap-2 ${
                  !selectedFile ||
                  isUploading ||
                  isUploadingLocal ||
                  (!selectedIndustry && selectedType !== "general")
                    ? "bg-slate-300 cursor-not-allowed"
                    : "bg-primary-600 hover:bg-primary-500"
                }`}
              >
                {(isUploading || isUploadingLocal) && (
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                )}
                {isUploading || isUploadingLocal
                  ? "Uploading Resume..."
                  : "Upload Resume"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  );
};

export default ResumeUploadModal;
